import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { getPendingConsultationReviews, moderateConsultationReview } from "../../Services/ConsultationReviewService";
import ReviewDetails from "./ReviewDetails";
import "./Reviews.css";

const ReviewModeration = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const saving = useRef(false);
  const heading = useRef(null);
  const actionButtons = useRef({});
  const focusTarget = useRef(null);

  useLayoutEffect(() => {
    if (!focusTarget.current) return;
    (actionButtons.current[focusTarget.current] || heading.current)?.focus();
    focusTarget.current = null;
  }, [reviews, rejectId]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try { setReviews(await getPendingConsultationReviews()); }
    catch { setLoadError(true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const moderate = async (review, approve) => {
    if (saving.current) return;
    saving.current = true;
    setBusy(review.id);
    setError("");
    setNotice("");
    try {
      await moderateConsultationReview(review.id, approve);
      const nextReview = reviews.find((item) => item.id !== review.id);
      focusTarget.current = nextReview ? `approve-${nextReview.id}` : "heading";
      setReviews((items) => items.filter((item) => item.id !== review.id));
      setRejectId(null);
      setNotice(approve ? "نظر تأیید شد و در بخش نظرات بیماران نمایش داده می‌شود." : "نظر رد شد و به‌صورت عمومی نمایش داده نمی‌شود.");
    } catch (failure) {
      setError(failure.status === 409 ? "وضعیت این نظر تغییر کرده است؛ فهرست را تازه‌سازی کنید." : "بررسی نظر ذخیره نشد. دوباره تلاش کنید.");
    } finally { saving.current = false; setBusy(null); }
  };

  return <section className="review-section iran-sans" dir="rtl" aria-labelledby="review-moderation-title">
    <div className="review-row"><h3 id="review-moderation-title" ref={heading} tabIndex={-1}>نظرات بیماران{!loading && !loadError && ` (${reviews.length.toLocaleString("fa-IR")})`}</h3><button type="button" className="review-button review-button--quiet" disabled={loading || busy !== null} onClick={load}>تازه‌سازی</button></div>
    <p className="review-muted">فقط نظرات تأییدشده در صفحه اصلی منتشر می‌شوند. تاریخ ثبت و نتیجه بررسی برای بیمار قابل مشاهده است.</p>
    {notice && <p className="review-success" role="status">{notice}</p>}
    {error && <p className="review-feedback" role="alert">{error}</p>}
    {loading ? <p className="review-muted" role="status">در حال دریافت نظرات مشاوره…</p> : loadError ? <p className="review-feedback" role="alert">دریافت نظرات ممکن نشد. با دکمه تازه‌سازی دوباره تلاش کنید.</p> : reviews.length === 0 ? <p className="review-muted">در حال حاضر نظری در انتظار بررسی نیست.</p> : <div className="review-list">
      {reviews.map((review) => <div className="review-list-item" key={review.id}>
        <ReviewDetails review={review} showPatient />
        {rejectId === review.id ? <div className="review-actions">
          <p className="review-muted w-100">این نظر منتشر نشود؟ نتیجه بررسی در حساب بیمار نمایش داده می‌شود.</p>
          <button type="button" className="review-button review-button--reject" ref={(element) => { actionButtons.current[`confirm-${review.id}`] = element; }} disabled={busy !== null} onClick={() => moderate(review, false)}>{busy === review.id ? "در حال ذخیره…" : "بله، رد نظر"}</button>
          <button type="button" className="review-button review-button--quiet" disabled={busy !== null} onClick={() => { focusTarget.current = `reject-${review.id}`; setRejectId(null); }}>انصراف</button>
        </div> : <div className="review-actions">
          <button type="button" className="review-button" ref={(element) => { actionButtons.current[`approve-${review.id}`] = element; }} disabled={busy !== null} onClick={() => moderate(review, true)}>{busy === review.id ? "در حال ذخیره…" : "تأیید و انتشار"}</button>
          <button type="button" className="review-button review-button--reject" ref={(element) => { actionButtons.current[`reject-${review.id}`] = element; }} disabled={busy !== null} onClick={() => { focusTarget.current = `confirm-${review.id}`; setRejectId(review.id); }}>رد نظر</button>
        </div>}
      </div>)}
    </div>}
  </section>;
};

export default ReviewModeration;
